// PRO-RATA ALLOCATION — the rules the parcel card and the reports share (Philip 2026-10-28).
//
// A parcel's parcel_prorata rows are allocation lines. Each line either points to one of the client's properties
// (property_id — it counts toward that property in reports) or does not (an airport's "No Rights", another occupant —
// free text, left out of reports). A set of lines totals 100%.
//   tax_year null  the parcel's split for every year
//   tax_year Y     an override for year Y only: when Y has one, its lines replace the every-year ones for Y
// ⛔ SAVED BY ONE DATABASE FUNCTION, ALL OR NOTHING (save_parcel_prorata). parcel_prorata is year-isolated
// (2026-10-28_parcel_prorata_lines_apply.sql): inside it each statement touches one set, never two years at once.
import { supabase } from '../supabase';

// The lines that apply to `year`: its override when it has one, else the every-year set.
// → { lines, yearOnly } — yearOnly says the lines are the year's own override.
export function prorataSet(rows, year) {
  const own = (rows || []).filter((r) => r.tax_year != null && String(r.tax_year) === String(year));
  return own.length ? { lines: own, yearOnly: true } : { lines: (rows || []).filter((r) => r.tax_year == null), yearOnly: false };
}

// The client's shares of a parcel-year — what the reports, the Properties list and the property card count. A line with
// a property counts toward it; a line without one is not the client's. No lines, or one 100% line to the parcel's own
// property, = no split (null): the parcel sits wholly on its own property, as it always did.
// → [{ property_id, pct }] for the parcel-year, or null for "no split".
export function prorataShares(lines, parcel, year) {
  const set = prorataSet((lines || []).filter((l) => l.parcel_id === parcel.id), year).lines;
  if (!set.length) return null;
  const shares = set.filter((l) => l.property_id).map((l) => ({ property_id: l.property_id, pct: pctOf(l.pro_rata_pct) }));
  if (shares.length === 1 && shares[0].property_id === parcel.property_id && shares[0].pct === 100) return null;
  return shares;
}

// For the Properties list and the property card: the parcels split in `year`, as Map(parcel_id → Map(property_id → %)).
// A parcel with no split that year is not in it.
export function splitsForYear(rows, parcels, year) {
  const byParcel = new Map();
  (rows || []).forEach((r) => byParcel.set(r.parcel_id, [...(byParcel.get(r.parcel_id) || []), r]));
  const out = new Map();
  (parcels || []).forEach((p) => {
    const sh = byParcel.has(p.id) ? prorataShares(byParcel.get(p.id), p, year) : null;
    if (sh) out.set(p.id, new Map(sh.map((x) => [x.property_id, x.pct])));
  });
  return out;
}

// The card's pick-list (Philip 2026-10-28): the client's properties that have a parcel under the SAME ASSESSOR as this
// parcel — the same assessor link (assessor_ref_id) when both have one, else the same assessor name — and always the
// parcel's own property. clientParcels: { property_id, assessor, assessor_ref_id } for the client's parcels.
export function sameAssessorProperties(props, clientParcels, parcel, homeId) {
  const name = (v) => String(v || '').trim().toLowerCase();
  const same = (x) => (parcel.assessor_ref_id && x.assessor_ref_id ? x.assessor_ref_id === parcel.assessor_ref_id
    : !!name(parcel.assessor) && name(x.assessor) === name(parcel.assessor));
  const ids = new Set((clientParcels || []).filter(same).map((x) => x.property_id));
  return (props || []).filter((p) => p.id === homeId || ids.has(p.id));
}

export const pctOf = (v) => parseFloat(String(v ?? '').replace(/[^0-9.]/g, '')) || 0;

// What stops a set of card lines saving, or null. Each line is a client property (once) or a named non-client line,
// and the set totals 100%.
export function prorataProblem(lines) {
  const sum = (lines || []).reduce((s, l) => s + pctOf(l.pro_rata_pct), 0);
  if (Math.abs(sum - 100) >= 0.01) return `Pro-rata allocations must total 100% (currently ${Math.round(sum * 100) / 100}%)`;
  if ((lines || []).some((l) => !l.property_id && !(l.prop_tenant || '').trim())) return 'Each pro-rata line needs a property, or a name for a line that is not the client\'s';
  const ids = (lines || []).map((l) => l.property_id).filter(Boolean);
  if (new Set(ids).size !== ids.length) return 'A property can have only one pro-rata line';
  return null;
}

// Replace one set — the every-year set (year null) or `year`'s override — with `lines`, and when `dropYear` is given
// remove that year's override (it now follows the every-year split). ONE DATABASE FUNCTION, ALL OR NOTHING
// (save_parcel_prorata, 2026-10-28_parcel_prorata_lines_apply.sql): a failure leaves the saved lines exactly as they
// were. `savedCount` / `dropCount` are how many lines those sets had when loaded — each delete must remove exactly those,
// or an RLS-blocked delete (0 rows, no error) would leave the old lines beside the new. → an error message, or null.
export async function saveProrataSet({ parcelId, year, lines, savedCount, dropYear = null, dropCount = 0 }) {
  const payload = lines.map((l) => ({
    property_id: l.property_id || null,
    prop_tenant: (l.prop_tenant || '').trim() || null,
    id_code: (l.id_code || '').trim() || null,
    pro_rata_pct: pctOf(l.pro_rata_pct),
  }));
  const { data, error } = await supabase.rpc('save_parcel_prorata', {
    p_parcel_id: parcelId, p_tax_year: year == null ? null : String(year), p_lines: payload, p_expected: savedCount,
    p_drop_year: dropYear == null ? null : String(dropYear), p_drop_expected: dropCount,
  });
  if (error) return `Pro-rata not saved: ${error.message}`;
  if (data !== payload.length) return `Pro-rata not saved: wrote ${data ?? 0} of ${payload.length} line(s)`;
  return null;
}
